import { Stack, Typography } from '@mui/material';
import React from 'react';
import { translateKey } from 'src/locales/translate';
import WarehouseBadge from 'src/sections/@dashboard/warehouse/components/WarehouseBadge';
import {
  formatDateOnly,
  formatProductStock,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { StockDisplayProduct } from 'src/sections/@dashboard/warehouse/utils/stockDisplay';
import { StockSummaryBatchRow } from './medicineStockBatchRows';

const s = (key: string, options?: Record<string, unknown>): string =>
  translateKey(`warehouse.medicineStock.${key}`, options);

interface MedicineStockBatchListProps {
  rows: StockSummaryBatchRow[];
  product: StockDisplayProduct;
}

/** The Batches holding stock: number, expiry, quantity; next out and expired marked. */
export default function MedicineStockBatchList({
  rows,
  product,
}: MedicineStockBatchListProps): React.ReactElement {
  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {s('noBatches')}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {rows.map(({ batch, nextOut, expired }) => (
        <Stack
          key={batch.id}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexWrap: 'wrap' }}
          data-testid="medicine-stock-batch"
        >
          <Typography
            variant="caption"
            color={expired ? 'text.secondary' : 'text.primary'}
          >
            {s('batch', {
              batch: batch.batchNumber,
              expiry: formatDateOnly(batch.expiryDate),
              quantity: formatProductStock(batch.onHandMilli, product),
            })}
          </Typography>
          {nextOut && (
            <WarehouseBadge
              label={s('nextOut')}
              hint={s('nextOutHint')}
              color="success"
              icon="solar:check-circle-bold"
              testId="medicine-stock-next-out"
            />
          )}
          {expired && (
            <WarehouseBadge
              label={s('expired')}
              hint={s('expiredHint')}
              color="error"
              icon="solar:danger-circle-bold"
              testId="medicine-stock-expired"
            />
          )}
        </Stack>
      ))}
    </Stack>
  );
}
